import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardRoute } from '../../utils/roleRoutes';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      setErrors({});
      
      try {
        const result = await login(formData.email, formData.password);
        
        if (result.success) {
          // Get user info from localStorage (set by login function)
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));
          const dashboardRoute = getDashboardRoute(userInfo?.role);
          navigate(dashboardRoute);
        } else {
          setErrors({ 
            general: result.message || 'Email ou senha inválidos' 
          });
        }
      } catch (error) {
        setErrors({ 
          general: 'Erro ao conectar com o servidor. Tente novamente.' 
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <Link to="/" className="logo-link">
            <img
              src="/logo/logo_mini.png"
              alt="MICA Capital"
              className="login-logo"
            />
          </Link>

          <h1 className="login-title">Entrar na sua conta</h1>
          <p className="login-subtitle">
            Acesse sua plataforma e transforme seu precatório em dinheiro em até 5 dias
          </p>

          {process.env.NODE_ENV === 'development' && (
            <div style={{
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#2C3E50',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}>
              <strong>Desenvolvimento:</strong><br />
              Email: admin.mica@capital.com<br />
              Senha: admin123
            </div>
          )}
          
          <form className="login-form" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="form-error-box">
                {errors.general}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="email" className="form-label">E-mail</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="form-label">Senha</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>
            
            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Lembrar de mim</span>
              </label>
              <a href="#" className="forgot-password">Esqueceu a senha?</a>
            </div>
            
            <button type="submit" className="btn btn-primary btn-block" disabled={isLoading}>
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
          
          <div className="login-divider">
            <span>ou</span>
          </div>
          
          <div className="login-footer">
            <p>
              Não tem uma conta? 
              <Link to="/registro" className="signup-link">Cadastre-se</Link>
            </p>
          </div>
        </div>
        
        <div className="login-info">
          <h2>Dinheiro na mão em até <span className="highlight-gold">5 Dias</span></h2>
          <p className="info-description">
            Nossa Inteligência Artificial analisa seu precatório e gera propostas em tempo recorde.
            Do cadastro ao dinheiro na conta: processo 100% digital e sem burocracia.
          </p>
          <ul className="info-list">
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>Análise por IA</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>Dinheiro em 5 Dias</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>100% Seguro</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>Processo 100% Digital</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Login;
